import { BUILDER_LESSON_STATUS, BUILDER_EVENT_TYPES as FIXED, MONTH_OF_YEAR } from '@/courses/values/courses.values';
import { ContentSchedule } from '@/tools/models/class-builder.models';

export class BuilderData {
  allContentLessons?: BuilderLesson[];
  allFillerLessons?: BuilderLesson[];
  allFunLessons?: BuilderLesson[];
  additionalLessons?: BuilderLesson[];
  allLessonsMap?: Map<number, BuilderLesson>;
  schoolDaysSlots?: Array<BuilderSchoolDaySlot>;
  schoolDaysSlotsMap?: Map<string, BuilderSchoolDaySlot>;
  scheduleType?: string;
  scheduleShortDay?: string;
  schedule?: Map<string, ContentSchedule[]>;
  subject?: any;
  automaticStructure? : any;
  rulesApplied?: BuilderRule[];
  componentsSelected?: any[];
  automaticFolderType?: string;
  schoolDay?: any;
  workingDays?: any[];
  specialDays?: any[];
  existFunLessons?: number = 0;
  coverImage?: any;
  objectsTagged?: any[];//TODO remove
  resourceId?: any;//TODO remove
  status?: string;
  builderId?: number;
  syncStatus?: string;
  version?: number;
  templateId?: number;
  contentId?: number;
  translateIds? : boolean;
  initialType?: string;
}

export class Structure {
  contents            : Map<number, any>;
  fillers             : Map<number, any>;
  funs                : Map<number, any>;
  additionals         : Map<number, any>;
  lessons             : Map<number, any>;
  contentRoot         : any;
  additionalRoot      : any;
  allContentLessons?  : BuilderLesson[];
  allFillerLessons?   : BuilderLesson[];
  allFunLessons?      : BuilderLesson[];
  additionalLessons?  : BuilderLesson[];
  allLessonsMap?      : Map<number, BuilderLesson>;
  materials?          : any;
  projects?           : any;
  deckLibrary?        : any;

  constructor(content: Map<number, any>, 
            filler: Map<number, any>, 
            fun: Map<number, any>, 
            additional: Map<number, any>, 
            lessons: Map<number, any>, 
            materials?: any, 
            projects? : any, 
            deckLibrary? : any,
            allContentLessons?: BuilderLesson[], 
            allFillerLessons?: BuilderLesson[], 
            allFunLessons?: BuilderLesson[], 
            additionalLessons?: BuilderLesson[], 
            allLessonsMap?: Map<number, BuilderLesson>) {
    this.contents = content;
    this.fillers = filler;
    this.funs = fun;
    this.additionals = additional;
    this.lessons = lessons;
    this.materials = materials;
    this.projects = projects;
    this.deckLibrary = deckLibrary;
    
    //Find root
    this.contentRoot = this.findRoot(this.contents, FIXED.CONTENT)
    this.additionalRoot = this.findRoot(this.additionals, FIXED.ADDITIONAL)

    this.allContentLessons = allContentLessons;
    this.allFillerLessons = allFillerLessons;
    this.allFunLessons = allFunLessons;
    this.additionalLessons = additionalLessons;
    this.allLessonsMap = allLessonsMap;
  }

  private findRoot(map: Map<number, any>, type: string) {
    for (const folder of map.values()) {
      if (folder.fixedSection === type) return {...folder, sections: []};
    }
    return undefined
  }
  
  public toSnapshot() {
    return {
        contents: this.contents,
        fillers: this.fillers,
        funs: this.funs,
        lessons: this.lessons,
        additionals: this.additionals,
        allContentLessons: this.allContentLessons,
        allFillerLessons: this.allFillerLessons,
        allFunLessons: this.allFunLessons,
        materials: this.materials,
        projects: this.projects,
        deckLibrary: this.deckLibrary,
    }
  }
}

export class BuilderLesson {
  id: number;
  name: string;
  rootType: string;
  duration: number;
  parent: Parent;
  startDay?: number;
  endDay?: number;
  startSchoolDate?: string;
  endSchoolDate?: string;
  pendingTime?: number;
  fixedOrder?: number;
  objectsComponent?: number[];
  status?: string;
  fixedSection : string;
  fixedSequence? : number;
  tagsSection? : string[];
  movedByBuilder?: boolean;
}

export class BuilderRule {
  type: string;
  time?: number;
  active?: boolean;
  //START_LESSON Rule properties
  startLesson?: number;
  finalLesson?: number;
  //PLACE_LABELS_ON_WORKING_DAY properties
  label?: string;
  workingDay?: string;
  //MOVE_LESSON Properties
  lessonSourceId?: number;
  lessonTargetId?: number;
  lessonDate?: string;
  //ADD Or REMOVE FUN
  funDate?: string;
  funDates?: string[];
}

export class Parent{
  weeks: string[] = [];
  months: string[] = [];
  parentId: number = null;      //The original parentId on the template
  // newParentId: string = null;   //The new parentId on the new builder structure

  constructor(parentId: number){
    this.parentId = parentId;
  }
}

const MS_PER_DAY = 86400000;
export class BuilderSchoolDaySlot {
  schoolDay: number;
  dayOfWeek: string;
  dayAB: string;
  weekId: string;
  monthId: string;
  date: Date;
  dateString: string;
  allowedTypes: string[];
  totalTime?: number;
  timeAvailable?: number;
  unusedTime?: number;
  lessons?: number[];
  status?: string;

  constructor(schoolDay: number, dateString :string, date : Date, dayOfWeek: string, dayAB: string, types: string[], totalTime: number) {
    this.schoolDay = schoolDay;
    this.dateString = dateString;
    this.date = date;
    this.allowedTypes = types;
    this.totalTime = totalTime;
    this.timeAvailable = totalTime;
    this.unusedTime = 0;
    this.lessons = [];
    this.dayOfWeek = dayOfWeek;
    this.dayAB = dayAB;
    this.monthId = this.buildMonthId(date)
    this.weekId = this.buildWeekId(date)
}

  /**
   * Calculate the week of the year in which a date occurs.
   * According to ISO 8601, the first week of the year is the one that includes the first Thursday of the year.
   * @param date 
   * @returns 
   */
  private buildWeekId(date: Date): string {
    const copiedDate = new Date(date.getTime());
    copiedDate.setHours(0, 0, 0, 0);
    copiedDate.setDate(copiedDate.getDate() + 4 - (copiedDate.getDay() || 7));  //Move to the Thursday of the same week.
    const yearStart = new Date(copiedDate.getFullYear(), 0, 1);
    const weekNumber = Math.ceil((((copiedDate.getTime() - yearStart.getTime()) / MS_PER_DAY) + 1) / 7);
    return `${copiedDate.getFullYear()}${weekNumber.toString().padStart(2, '0')}`;
  }

  private buildMonthId(date: Date): string {
    // return `${date.getFullYear()}${(date.getMonth() + 1).toString().padStart(2, '0')}`;
    return `${MONTH_OF_YEAR[date.getMonth()]}-${date.getFullYear()}`

  }
}
